import { CARD_MAX_LEVEL, isCardSlot } from '@kenocyber/shared';
import { CARD_CHARGE_FULL_BPS } from '../cards/balance';
import { CARD_EFFECT_SLOTS } from '../cards/effects';
import type { AtomicBombSelectionWire } from '../damage/atomic-bomb';
import { LIFESTEAL_CARRY_MODULUS } from '../damage/lifesteal';
import { POISON_TICK_COUNT } from '../damage/poison';
import { isOutgoingProcId, type JewelProcSelectionWire } from '../damage/proc-lottery';
import { SAW_MAX_BLADES, SAW_ORBIT_PHASE_UNITS } from '../damage/saw';
import type { SimulationRngState } from '../simulation-rng';
import type { CardAbilityState } from './card-abilities';
import { assertProgression, type ProgressionState } from './progression';
import type {
  BleedingMark,
  BossEntity,
  HuntEvent,
  HuntPhase,
  HuntSawContact,
  HuntSummonEntity,
  MissileEntity,
  MobEntity,
  PetEntity,
  PoisonStack,
  ProjectileEntity,
} from './simulation';

/**
 * Versioned, serialisable state of a hunt.
 *
 * This is what lets the SERVER be authoritative without a request per shot:
 * it stores the state it last validated, and on the next checkpoint it
 * restores that state, advances its own simulation by the time its own clock
 * allows, and compares nothing with what the client claims.
 *
 * ## What belongs here
 *
 * Everything that changes the FUTURE of the run: tick, RNG streams, entities,
 * timers, accumulators, progression and the metric windows. Nothing visual -
 * no Phaser objects, textures, glow, camera or tween state. A snapshot must
 * mean the same thing on a server with no renderer at all.
 *
 * ## Numbers
 *
 * Progression and the session's cumulative XP/Gold are **decimal strings**:
 * they are `bigint` in memory and there is no level cap, so a JSON number
 * would silently stop being exact long before the curve does. Positions,
 * timers and damage stay `number` - they are physics, not balances.
 *
 * ## Trust
 *
 * A snapshot is written and read by the server, and read by the client during
 * a divergence correction. It is validated every time: a row that was
 * corrupted, hand-edited or written by an older build must fail loudly instead
 * of silently producing a run nobody can reproduce. Parsing never executes
 * anything from the JSON.
 */

/**
 * Version 3: o dano de um projétil viaja em MILLI e como string decimal.
 *
 * A versão 2 guardava `damage` em pontos, como número. Deixou de chegar: um
 * tiro com Corações Primordiais incorporados vale 40,4 pontos e a contagem não
 * tem tecto, portanto nem a fração nem os quarenta dígitos cabem num `number`.
 *
 * Uma sessão da versão 2 é RECUSADA em vez de lida com folga. É a mesma
 * política que a versão 2 usou quando chegou: um documento antigo é superado no
 * próximo bootstrap, depois de a sua cauda ser liquidada com o que ela
 * realmente teve. Nenhum item, Gold, XP, nível ou ponto é tocado.
 *
 * ---
 *
 * Versão 4: o fluxo de procs de saída, e o proc congelado de cada projétil
 * (gate V28G.G).
 *
 * ## Porque um campo ADITIVO obriga na mesma a subir o número
 *
 * Porque a versão não descreve a forma do documento - descreve o que um build
 * precisa de saber para CONTINUAR a corrida que ele guarda. Os dois campos
 * novos decidem o futuro: um diz onde a loteria vai buscar o próximo sorteio, o
 * outro diz que tiros já em voo trazem um corte.
 *
 * Um build anterior a este gate aceitaria um documento da versão 3 escrito por
 * este, porque o número dir-lhe-ia que era seu - e deitaria os dois campos
 * fora em silêncio. A corrida seguiria sem procs e com outro consumo de
 * entropia, e o digest do cliente deixaria de bater com o do servidor a cada
 * checkpoint. É exatamente o "aceitar um documento como sendo outro" que o
 * campo `version` existe para tornar impossível.
 *
 * Isso distingue-o de `rng.petTarget`, que é opcional e NÃO subiu a versão: um
 * build que o ignorasse reconstruía-o da semente da sessão e chegava ao mesmo
 * fluxo. Aqui não há reconstrução possível - o número de valores gastos por
 * disparo mudou.
 *
 * ## A política para a versão 3 é a mesma que para a 2: RECUSA
 *
 * Não há upgrade, e escrever um seria dar a projéteis legados um campo que a
 * simulação que os disparou nunca conheceu. Um documento da versão 3 é superado
 * no próximo bootstrap, depois de a sua cauda ser liquidada com o que ele
 * realmente teve - e nenhum tiro dessa corrida recebe um proc retroativo,
 * porque nenhum tiro dessa corrida é adotado.
 *
 * ---
 *
 * Versão 5: o proc do projétil passa a carregar a MAGNITUDE congelada
 * (gate V28G.H).
 *
 * A versão 4 guardava `{ id, entropy }` e a simulação recalculava o corte no
 * impacto, a partir do dano-base de então. Isso fazia um ponto de dano comprado
 * com o tiro no ar mudar o corte de um projétil já disparado - a mesma falha
 * que `damageMilli` deixou de ter no gate V28F.B, sobrevivendo num campo ao
 * lado dele.
 *
 * O documento passa a guardar `cutMilli`, que é o RESULTADO e não a receita.
 * Uma versão 4 é recusada, como a 3 e a 2: os seus projéteis não trazem a
 * magnitude, e o único modo de a inventar seria reavaliá-la com um perfil que
 * pode já não ser o do disparo - que é precisamente o defeito que esta versão
 * corrige. Nenhuma corrida antiga é adotada, portanto nenhum tiro antigo recebe
 * um corte que não trazia.
 *
 * ---
 *
 * Versão 6: a corrida ganha o resto do Roubo de Vida, e passa a curar
 * (gate V28G.I).
 *
 * ## Porque um campo que quase sempre vale zero obriga a subir o número
 *
 * Porque a versão não descreve a forma do documento - descreve o que um build
 * precisa de saber para CONTINUAR a corrida que ele guarda. E mudaram duas
 * coisas ao mesmo tempo:
 *
 * - o documento passa a guardar `lifestealCarryBps`, a fração de milésimo de
 *   ponto que a percentagem já ganhou e ainda não entregou;
 * - a MESMA corrida, com o MESMO equipamento, passa a curar. Um build anterior
 *   a este gate aceitaria um documento da versão 5 escrito por este, deitava o
 *   resto fora, e continuava a caçada sem cura nenhuma - com uma vida que
 *   divergia da do servidor a cada golpe, e um digest que deixava de bater a
 *   cada checkpoint.
 *
 * ## A política continua a ser RECUSA
 *
 * Não há atualização, e escrever uma seria dar a uma corrida antiga um resto
 * que ela nunca teve. Uma versão 5 é superada no bootstrap seguinte, depois de
 * a sua cauda ser liquidada com o que ela realmente teve - e nenhum golpe dessa
 * corrida recebe cura retroativa, porque nenhum golpe dessa corrida é adotado.
 *
 * ## Versão 7 - o Núcleo Atômico (gate V28G.J)
 *
 * Duas coisas novas, e as duas decidem o FUTURO da corrida:
 *
 * - um projétil pode ser uma **bomba**, com a magnitude da explosão congelada.
 *   Um build da versão 6 lia-a como um tiro comum: ela voava depressa demais,
 *   perseguia, batia num alvo em vez de detonar por proximidade, e não explodia
 *   - portanto perder este campo não perde um efeito, troca uma entidade por
 *   outra e faz a re-simulação do servidor matar outros mobs;
 * - a corrida carrega o **fluxo da rolagem por salva**. Sem ele, uma reconexão
 *   recomeçava a sequência do princípio.
 *
 * A política continua a ser RECUSA, pela mesma razão da versão 6.
 *
 * ## Versão 8 - o Arsenal Ígneo (gate V28G.L)
 *
 * Duas coisas novas, e as duas decidem o FUTURO da corrida:
 *
 * - a corrida carrega os **mísseis em voo**, cada um com a sua posição,
 *   direção, alvo, tempo restante, identidade e dano congelado. Um build da
 *   versão 7 não os lia de todo - eles desapareciam da corrida - e a
 *   re-simulação do servidor mataria outros mobs;
 * - o **fluxo de seleção de alvo**. Sem ele, um míssil restaurado escolheria
 *   outro inimigo do que o que a corrida tinha escolhido, e o digest deixaria de
 *   bater ao primeiro retarget.
 *
 * A política continua a ser RECUSA, pela mesma razão da versão 6: uma versão 7 é
 * superada no bootstrap seguinte, depois de a sua cauda ser liquidada com o que
 * ela realmente teve, e nenhum tiro dessa corrida recebe um míssil retroativo.
 *
 * ## Versão 9 - o Orbe da Víbora (gate V28G.M)
 *
 * Três coisas novas, e as três decidem o FUTURO da corrida:
 *
 * - a corrida carrega os **stacks de veneno ativos**, cada um com a sua
 *   identidade, o seu alvo, o total congelado, o instante em que foi aplicado e
 *   quantas das seis parcelas já pagou. Um build da versão 8 não os lia de todo -
 *   eles desapareciam da corrida, com até seis segundos de dano autoritativo por
 *   cobrar - e a re-simulação do servidor mataria outros mobs;
 * - a corrida carrega o seu **contador de identidade**. Sem ele, uma retoma daria
 *   a um stack novo o id de um que ainda está a cobrar, e a cena passaria a
 *   desenhar duas coisas diferentes como se fossem a mesma;
 * - o **fluxo do veneno**. Sem ele, o acerto seguinte a uma reconexão repetiria
 *   uma rolagem que a corrida já tinha gasto.
 *
 * A política continua a ser RECUSA, pela mesma razão da versão 6.
 *
 * ## Versão 10 - a Garra Hemorrágica (gate V28G.N)
 *
 * Três coisas novas, e as três decidem o FUTURO da corrida:
 *
 * - a corrida carrega as **marcas de sangramento ativas**, cada uma com a sua
 *   identidade, o seu alvo, o dano congelado e o instante em que foi aplicada.
 *   Um build da versão 9 não as lia de todo - elas desapareciam da corrida, com
 *   dano acumulado que só o jogador podia gastar - e a re-simulação do servidor
 *   mataria outros mobs no dia em que a ordem chegasse;
 * - a corrida carrega o seu **contador de identidade**. Sem ele, uma retoma
 *   daria a uma marca nova o id de uma que ainda existe, e a cena passaria a
 *   desenhar duas coisas diferentes como se fossem a mesma;
 * - o **fluxo do sangramento**. Sem ele, o acerto seguinte a uma retoma
 *   repetiria uma rolagem que a corrida já tinha gasto.
 *
 * A política continua a ser RECUSA, pela mesma razão da versão 6.
 *
 * ## A versão 12 acrescenta um campo, e é um fluxo (gate V28G.U)
 *
 * O **fluxo do Coração Vital**. Sem ele, a salva seguinte a uma retoma repetiria
 * uma rolagem que a corrida já tinha gasto - e uma corrida que recomeça a
 * sequência de cura do princípio é uma corrida onde uma reconexão vale vida.
 *
 * Nada mais entra: a cura não guarda resto - ela nasce em milli inteiro, porque
 * a magnitude divide uma vez e no fim - não guarda pedido pendente, não guarda
 * alvo e não guarda relógio. A vida que ela pôs já está em `health`, que a
 * instantânea sempre carregou.
 *
 * A política continua a ser RECUSA, pela mesma razão da versão 6.
 *
 * ## Versão 13 - as CARTAS (gate V28H.H)
 *
 * Um campo novo, e cada parte dele decide o futuro da corrida:
 *
 * - o **cooldown restante** de cada espaço. Sem ele, uma retoma devolveria uma
 *   carta pronta - e um F5 a meio de uma espera de 35 s valeria uma activação;
 * - o **tempo activo restante**. Sem ele, uma habilidade a meio dos seus seis
 *   segundos ou desapareceria ou recomeçaria do princípio;
 * - o **acumulador de disparo** de cada espaço. Sem ele, a salva seguinte a uma
 *   reconexão sairia no instante errado - cedo de mais ou tarde de mais - e a
 *   re-simulação do servidor mataria outros mobs;
 * - a **identidade congelada**: o espaço, a definição, a instância e o nível. É
 *   o que torna a corrida re-simulável sem voltar a ler o inventário, e é a
 *   mesma disciplina do loadout, das passivas e do pet.
 *
 * Nada de visual entra: não há sprite, tween, clarão, partícula nem objecto de
 * pool aqui. A posição dos dois canhões é DERIVADA da nave a cada leitura, e
 * por isso não é estado.
 *
 * A política continua a ser RECUSA, pela mesma razão da versão 6: uma versão 12
 * é superada no bootstrap seguinte, depois de a sua cauda ser liquidada com o
 * que ela realmente teve, e nenhuma salva dessa corrida recebe uma habilidade
 * retroactiva.
 *
 * ## Versão 15 - o Bastião saiu da caça (gate V28I.C)
 *
 * Nenhum campo mudou de forma, e é por isso que esta subida merece explicação.
 *
 * O que mudou foi o SIGNIFICADO de uma ranhura que já existia. `cardEffects`
 * guarda oito temporizadores, e um deles - `untargetable` - só podia ter sido
 * escrito nesta arena pelo Bastião. Ele passou a ser somente do duelo, portanto
 * um documento da versão 14 pode trazer segundos de invulnerabilidade que já
 * nenhuma carta desta arena sabe conceder.
 *
 * Duas coisas correm por causa disso, e as duas justificam o número:
 *
 * - o **leitor zera essa ranhura**, sempre. Um documento antigo não ressuscita
 *   uma invulnerabilidade que a arena já não tem, e sem isso um jogador que
 *   recarregasse a página no meio dos quatro segundos ficava com eles - e o
 *   servidor, ao re-simular a janela a partir do mesmo documento, chegava a
 *   outro resultado, porque a activação que os produziu já é recusada;
 * - a **re-simulação diverge** para qualquer corrida que tenha usado a carta. O
 *   servidor repete a janela a partir da instantânea, e as regras que ele aplica
 *   são as de hoje: uma salva que passou por baixo do escudo antigo passaria a
 *   acertar. Recusar a versão 14 é o que impede uma cauda inteira de ser
 *   liquidada com dois resultados diferentes.
 *
 * A política continua a ser RECUSA, e aqui ela é a única resposta honesta.
 */
export const HUNT_SNAPSHOT_VERSION = 15;

/**
 * Um projétil como o DOCUMENTO o guarda.
 *
 * Igual à entidade viva em tudo menos em três campos, e os três pela mesma
 * razão: `damageMilli`, `splashDamageMilli` e o `cutMilli` do proc são `bigint`
 * na simulação - nem a contagem de corações nem o dano-base têm tecto - e uma
 * instantânea é JSON, que não tem `bigint`. Atravessam como string decimal,
 * exatamente como um saldo.
 *
 * Um tipo próprio em vez de reutilizar `ProjectileEntity`: os dois deixaram de
 * ser a mesma coisa, e partilhar o tipo faria `JSON.stringify` atirar em
 * produção em vez de o compilador recusar aqui.
 */
export type HuntSnapshotProjectile = Omit<
  ProjectileEntity,
  'damageMilli' | 'splashDamageMilli' | 'jewelProc' | 'atomicBomb'
> & {
  readonly damageMilli: string;
  readonly splashDamageMilli?: string;
  readonly jewelProc: JewelProcSelectionWire | null;
  /**
   * A bomba congelada, ausente num tiro comum (versão 7).
   *
   * `blastMilli` é `bigint` pela mesma razão que `damageMilli` é - deriva do
   * dano-base autoritativo, que não tem tecto - portanto atravessa como string
   * decimal. Opcional e não `| null` porque a entidade viva também o é: um tiro
   * de pet nunca tem um, e escrever `atomicBomb: null` em cada bala de cada pet
   * mudaria o digest canónico de toda a corrida sem acrescentar informação.
   */
  readonly atomicBomb?: AtomicBombSelectionWire;
};

/**
 * Um MÍSSIL como o documento o guarda (versão 8).
 *
 * Igual à entidade viva num campo só, e pela razão de sempre: `damageMilli` é
 * `bigint` na simulação - deriva do dano-base autoritativo, que não tem tecto -
 * e uma instantânea é JSON. Atravessa como string decimal, exatamente como um
 * saldo e como o dano de um projétil.
 */
export type HuntSnapshotMissile = Omit<MissileEntity, 'damageMilli'> & {
  readonly damageMilli: string;
};

/**
 * Um STACK de veneno como o documento o guarda (versão 9).
 *
 * Igual à entidade viva num campo só, e pela razão de sempre: `totalDamageMilli`
 * é `bigint` na simulação - deriva do dano-base autoritativo, que não tem tecto -
 * e uma instantânea é JSON. Atravessa como string decimal, exatamente como um
 * saldo, o dano de um projétil e o de um míssil.
 *
 * ## Porque não guarda "próximo tick" nem "parcelas restantes"
 *
 * Porque são funções exatas de `appliedAt` e `ticksDone`: a parcela `i` vence em
 * `appliedAt + i x 1000` ms, e faltam `6 - ticksDone`. Guardá-los seria guardar o
 * mesmo facto três vezes num documento que atravessa disco e um digest, e três
 * cópias de um facto são duas oportunidades de ele passar a discordar de si
 * mesmo. `damage/poison.ts` expõe as duas leituras como funções.
 */
export type HuntSnapshotPoisonStack = Omit<PoisonStack, 'totalDamageMilli'> & {
  readonly totalDamageMilli: string;
};

/**
 * Uma marca de sangramento como o DOCUMENTO a guarda (gate V28G.N).
 *
 * `damageMilli` atravessa como STRING decimal pela regra de sempre: ele
 * deriva do dano-base autoritativo, que não tem tecto, e uma instantânea é JSON.
 *
 * ## Porque não guarda prazo nenhum
 *
 * Porque não existe. Uma marca não expira, não se renova e não cobra parcela: o
 * que a acaba é uma ordem do jogador. Um campo de tempo restante aqui seria uma
 * promessa que nada cumpriria, e um leitor futuro acabaria por lhe dar
 * significado.
 */
export type HuntSnapshotBleedingMark = Omit<BleedingMark, 'damageMilli'> & {
  readonly damageMilli: string;
};

/**
 * Um relógio de contacto de serra como o DOCUMENTO o guarda (gate V28G.P).
 *
 * Idêntico ao vivo, porque não há nada nele que o JSON não saiba dizer: três
 * números, e nenhum deles é `bigint`. O tipo existe na mesma - em vez de um
 * `HuntSawContact` reutilizado - porque o documento e a simulação são duas
 * fronteiras diferentes, e partilhar o tipo faria uma mudança na segunda
 * atravessar a primeira sem que nada a assinalasse.
 */
export type HuntSnapshotSawContact = HuntSawContact;

export interface HuntSnapshotProgression {
  readonly level: string;
  readonly xpIntoLevel: string;
  readonly lifetimeXp: string;
  readonly gold: string;
  readonly freePoints: string;
  readonly damagePoints: string;
  readonly healthPoints: string;
  readonly attackSpeedPoints: string;
}

/**
 * Cumulative totals for the session, never reset by a level-up or a death.
 *
 * The server diffs these across a checkpoint to learn what the window earned.
 * XP and Gold are decimal strings for the same reason as the progression;
 * damage is physics and kills are a count.
 */
export interface HuntSnapshotEarned {
  readonly xp: string;
  readonly gold: string;
  readonly damage: number;
  readonly kills: number;
  /**
   * Normal mobs killed this session, bosses excluded.
   *
   * Not a statistic: both normal rewards accrue as fractions of it - 4 XP per
   * 3 kills and 1 Gold per 2 - and this counter is what carries them across a
   * checkpoint, a reload, a spent point or a death. Losing it would round an
   * unfinished group down to nothing.
   */
  readonly normalKills: number;
  /**
   * Bosses killed this session.
   *
   * Authoritative for the same reason `normalKills` is: an artifact's Gold
   * percentage turns the boss reward into a fraction too, and the cumulative
   * counter is what carries the remainder across a checkpoint, a reload, a
   * combine or a death instead of rounding each boss down.
   */
  readonly bossKills: number;
  /**
   * Gold the bosses of this session are owed BEFORE any bonus, cumulative.
   *
   * Not derivable from `bossKills` any more: a tier 6 is worth six times a
   * tier 1, so the running total has to be carried rather than recomputed from
   * a count. Kept as a decimal string like every other currency on the wire,
   * and summed as `bigint` - the Receptor's thousandths of a Gold survive
   * because this is a cumulative base that is floored once, not a per-boss
   * amount that is rounded six times.
   *
   * Optional: a snapshot written before tiers existed has none, and its bosses
   * were all worth the flat 60, which is exactly what the fallback computes.
   */
  readonly bossGoldBase?: string;
}

/**
 * The encounter a run is bound to, as the snapshot carries it.
 *
 * Deliberately NOT the whole database row. The delay it was drawn with, when
 * it was created and what it superseded are audit, and audit belongs in the
 * table. What is here is only what changes the outcome of the next tick.
 */
export interface HuntSnapshotBossEncounter {
  /** Opaque and stable across a reload, a death and a re-simulation. */
  readonly encounterId: string;
  /** 1-based, per account, phase and map. */
  readonly ordinal: number;
  /**
   * 1..6, or `null` when it has not been revealed yet.
   *
   * The server always knows it. The BROWSER does not, until the server has
   * confirmed that the boss reached the field - because a tier is what the
   * next boss is worth, and knowing it in advance is an invitation to stop
   * playing until a good one comes up.
   *
   * Nullable rather than absent so the shape is the same on both sides, and so
   * "not revealed" is a value somebody had to write rather than a field that
   * went missing.
   */
  readonly tier: number | null;
  /**
   * True while the boss is on the field.
   *
   * Cleared by a death, along with the entity, because the boss is genuinely
   * gone from the arena until the recovery window ends.
   */
  readonly activated: boolean;
  /**
   * True while the same boss is waiting to come back after killing the player.
   *
   * Distinct from a scheduled encounter that has simply not arrived: this one
   * has already been fought, its tier is decided, its attempt count is higher,
   * and the countdown running is a RECOVERY window rather than the delay it
   * was drawn with. Nothing is re-drawn while it is true.
   *
   * Optional so a snapshot written before recovery existed parses as `false`,
   * which is exactly what such a run was.
   */
  readonly retrying?: boolean;
}

export interface HuntSnapshotTimedAmount {
  readonly t: number;
  readonly amount: number;
}

export interface HuntSnapshot {
  readonly version: number;
  /** Simulated time, ms. The authoritative "tick" of the session. */
  readonly tickMs: number;
  /**
   * **Tempo de deriva congelado pela carga**, em ms (gate V28H.N).
   *
   * A Lança Solar prende o movimento enquanto carrega; na caça isso é prender a
   * deriva. Decide o FUTURO - onde a nave está no próximo tique - portanto
   * atravessa a instantânea. Sem ele, uma reconexão a meio de uma carga
   * devolveria a nave a um sítio onde ela não estava.
   */
  readonly driftFrozenMs: number;
  /**
   * **As oito ranhuras de efeito de carta** (gate V28H.T).
   *
   * ## O buraco que este campo fecha
   *
   * Elas NUNCA foram guardadas. O Bastião ligava o temporizador dele no
   * servidor, a instantânea saía sem ele, e o efeito evaporava-se na
   * restauração seguinte - do lado do servidor E do lado do cliente. Uma nave
   * com o Bastião ligado continuava a levar dano de uma fuga, e o ecrã nunca
   * teve como saber que a carta estava a correr.
   *
   * O `CLAUDE.md` já o exigia por escrito: a instantânea contém **tudo o que
   * afecta o futuro** de uma corrida. Um temporizador que decide se a nave leva
   * dano afecta o futuro, e faltava.
   *
   * Pela ordem de `CARD_EFFECT_SLOTS`, que é a mesma que o duelo já usa - oito
   * pares, e não oito chaves com nome: o nome está na posição, e uma lista
   * posicional é o que impede as duas metades de discordarem sobre a ordem.
   *
   * A ausência é OITO RANHURAS VAZIAS e não uma recusa: um documento escrito
   * antes deste gate descreve uma corrida em que nenhum efeito podia estar a
   * correr, e vazio é o estado exacto dessa corrida.
   */
  readonly cardEffects: readonly { readonly ms: number; readonly bps: number }[];
  /**
   * **As bombas de carta em voo** (gate V28H.T).
   *
   * A Esfera Ígnea e o Catolé deixaram de rebentar no instante da ordem e
   * passaram a VIAJAR. Elas decidem o futuro - quem leva dano, e quando -
   * portanto atravessam a instantânea, como qualquer projéctil.
   *
   * O dano é uma string decimal, como todo o milli deste formato: um `bigint`
   * não sobrevive a um JSON, e um `number` perderia dígitos que a economia tem.
   *
   * A ausência é a LISTA VAZIA: um documento anterior descreve uma corrida em
   * que nenhuma podia estar em voo.
   */
  readonly cardBlasts: readonly {
    readonly id: number;
    readonly kind: 'fireball' | 'catole';
    readonly x: number;
    readonly y: number;
    readonly targetId: number;
    readonly speed: number;
    readonly radius: number;
    readonly damageMilli: string;
    readonly ageMs: number;
  }[];
  readonly pendingMs: number;
  readonly runStartMs: number;
  readonly phase: HuntPhase;
  readonly nextEntityId: number;
  readonly facing: number;
  readonly health: number;
  readonly spawnAccumulatorMs: number;
  /**
   * Milli-health The Cure has healed without yet delivering a whole point.
   *
   * Authoritative: regeneration is a rate, and a 16 ms tick at 1 hp/s is 16
   * thousandths of a point. Dropping the remainder would make the artifact
   * heal nothing at all, so it is carried here like the Gold fractions are.
   */
  readonly regenCarryMilli: number;
  /**
   * O resto SUB-MILLI do Roubo de Vida, em `[0, 10 000)` (gate V28G.I).
   *
   * Autoritativo pela mesma razão que `regenCarryMilli` é: decide o futuro. A
   * percentagem de um golpe produz uma fração de um MILÉSIMO de ponto de vida,
   * e uma corrida restaurada sem ele voltaria a perder essa fração a cada
   * golpe - a joia curaria menos do que curou, e o digest do cliente deixaria
   * de bater com o do servidor.
   *
   * Obrigatório desde a versão 6, sem `?? 0`: todo o documento que chega aqui
   * foi escrito por um build que tem a joia, porque a versão 5 é RECUSADA. Um
   * valor por omissão seria a porta por onde uma corrida antiga entrava com um
   * resto que ela nunca teve.
   */
  readonly lifestealCarryBps: number;
  /**
   * Moments still to come in the current spawn window, ms from its start.
   *
   * Part of the authoritative state because it decides the future: the server
   * re-simulating a window has to release the same arrivals at the same
   * instants the browser did. Without it a restored run would either drop
   * enemies or bunch them at the next boundary.
   */
  readonly spawnSchedule: readonly number[];
  readonly attackAccumulatorMs: number;
  readonly bossCountdownMs: number;
  readonly rng: {
    readonly spawn: SimulationRngState;
    /**
     * The boss delay stream.
     *
     * It no longer decides anything: the delay is drawn by the server with
     * cryptographic entropy and persisted on the encounter, because a public
     * seed that an F5 re-mints is a seed a player can shop with. The stream is
     * still carried so a snapshot round-trips unchanged and so restoring one
     * written before encounters existed does not have to invent state.
     */
    readonly bossTimer: SimulationRngState;
    /**
     * Which enemies the pet's cannons pick.
     *
     * Optional because it did not exist before the Abyssal Drone could spread
     * a volley across several mobs. A snapshot without it is rebuilt with a
     * fresh stream derived from the session's own seed - deterministic, and
     * identical on the server, which is what matters.
     */
    readonly petTarget?: SimulationRngState;
    /**
     * A loteria de procs de saída desta corrida (gate V28G.G).
     *
     * **Obrigatório**, ao contrário de `petTarget`, e é a versão 4 que o
     * permite: todo o documento que este build lê foi escrito por ele, portanto
     * o campo está sempre lá. Torná-lo opcional seria manter aberta a porta que
     * a subida de versão fechou - a de um documento sem fluxo ser aceite como
     * se tivesse um.
     *
     * Uma reconexão RETOMA este estado em vez de o recriar da semente. Recriar
     * repetiria a sequência desde o princípio, e o disparo seguinte devolveria
     * um proc que a corrida já tinha gasto.
     */
    readonly jewelProc: SimulationRngState;
    /**
     * A rolagem POR SALVA do Núcleo Atômico (gate V28G.J).
     *
     * **Obrigatório**, e é a versão 7 que o permite - a mesma disciplina de
     * `jewelProc`. Um fluxo próprio e não uma entrada partilhada: uma salva
     * consome uma rolagem daqui e vinte e oito do outro, e juntá-los faria cada
     * salva deslocar a sequência de procs.
     */
    readonly volleyBomb: SimulationRngState;
    /**
     * A seleção de ALVO do Arsenal Ígneo (gate V28G.L).
     *
     * **Obrigatório**, e é a versão 8 que o permite - a mesma disciplina de
     * `jewelProc` e de `volleyBomb`. Um fluxo próprio porque a finalidade é
     * outra e a cadência também: a rolagem por salva acontece em todas as
     * salvas, e a escolha de alvo só quando um míssil nasce ou perde o alvo.
     * Juntá-los faria uma perseguição deslocar a sequência de procs.
     */
    readonly missileTarget: SimulationRngState;
    /**
     * A rolagem do Orbe da Víbora (gate V28G.M).
     *
     * **Obrigatório**, e é a versão 9 que o permite - a mesma disciplina de
     * `jewelProc`, `volleyBomb` e `missileTarget`. Um fluxo próprio porque a
     * UNIDADE é outra: a loteria ofensiva sorteia por projétil NASCIDO e esta
     * sorteia por acerto direto CONFIRMADO, portanto uma salva de vinte e um
     * tiros que acerta em três consome vinte e um de um e três do outro.
     * Juntá-los faria cada acerto deslocar os procs de todos os tiros seguintes.
     */
    readonly poison: SimulationRngState;
    /**
     * A rolagem da Garra Hemorrágica (gate V28G.N).
     *
     * **Obrigatório**, e é a versão 10 que o permite - a mesma disciplina dos
     * quatro acima. Um fluxo próprio e separado do do veneno em particular
     * porque as duas joias podem produzir no MESMO acerto direto: partilhá-lo
     * faria uma corrida com a Garra ter outra sequência de veneno que uma sem
     * ela, e a diferença não seria da joia.
     */
    readonly bleeding: SimulationRngState;
    /**
     * A rolagem do DIAMANTE SERRILHADO (gate V28G.P).
     *
     * **Obrigatório**, e é a versão 11 que o permite - a mesma disciplina dos
     * cinco acima. Um fluxo próprio, e com uma razão que só ele tem: a cadência
     * dele é ditada pelo RELÓGIO e não por um acontecimento de combate. Ele
     * avança enquanto uma lâmina estiver encostada a um inimigo, tenha a nave
     * disparado ou não - portanto partilhá-lo faria a sequência de procs, veneno
     * e sangramento depender de quanto tempo as serras estiveram em contacto.
     */
    readonly saw: SimulationRngState;
    /**
     * A rolagem do CORAÇÃO VITAL (gate V28G.U).
     *
     * **Obrigatório**, e é a versão 12 que o permite - a mesma disciplina dos
     * seis acima. Um fluxo próprio, e com a razão que o gate escreve por extenso:
     * a cura não pode deslocar a sequência de nenhum outro sistema, e um fluxo
     * partilhado é exatamente a maneira de a deslocar sem que nada o assinale.
     *
     * Uma reconexão RETOMA este estado em vez de o recriar da semente. Recriar
     * repetiria a sequência desde o princípio, e a salva seguinte devolveria uma
     * cura que a corrida já tinha gasto.
     */
    readonly vitalHeart: SimulationRngState;
  };
  /**
   * The boss encounter this run is bound to, or null between encounters.
   *
   * Everything here decides the FUTURE, which is why it is in the snapshot and
   * not merely in the database: the tier decides what the boss will be worth
   * and how big it is drawn, and `activated` decides whether a death is
   * followed by the same boss returning or by the countdown resuming.
   *
   * Null means "no boss is scheduled" - the state a run is in for at most one
   * settlement window after a defeat, until the server draws the next one. The
   * countdown does not tick while it is null, so nothing is lost and nothing
   * spawns unattended.
   */
  readonly bossEncounter: HuntSnapshotBossEncounter | null;
  readonly mobs: readonly MobEntity[];
  readonly boss: BossEntity | null;
  readonly projectiles: readonly HuntSnapshotProjectile[];
  /**
   * Os mísseis em voo (gate V28G.L).
   *
   * Uma lista à parte dos projéteis porque é uma entidade à parte na simulação -
   * ver `MissileEntity` - e porque a forma é outra: um míssil tem um alvo
   * MUTÁVEL e não tem `orphaned`, `targetId` readonly, proc, bomba nem raio de
   * explosão. Enfiá-lo na outra lista obrigaria cada tiro comum e cada bala de
   * pet a carregar campos que não usam, e mudaria o digest canónico de toda a
   * corrida para descrever a entidade mais rara que existe.
   */
  readonly missiles: readonly HuntSnapshotMissile[];
  /**
   * Os stacks de veneno ativos, em ordem de criação (gate V28G.M).
   *
   * Uma lista à parte dos mobs e do boss porque um stack não é um inimigo: ele
   * tem o seu próprio relógio, existem quantos forem sobre o mesmo alvo, e
   * pendurá-lo na entidade obrigaria cada mob de cada vaga a carregar um array
   * vazio - mudando o digest canónico de toda a corrida para descrever a coisa
   * mais rara que existe.
   *
   * **Sem teto de quantidade.** O contrato é explícito, e um limite aqui seria
   * uma decisão de equilíbrio tomada por um validador.
   */
  readonly poison: readonly HuntSnapshotPoisonStack[];
  /**
   * A identidade do próximo stack (gate V28G.M).
   *
   * Um contador próprio, ao lado de `nextEntityId` e nunca partilhado com ele:
   * um stack não é uma entidade da arena, e partilhar o contador faria cada
   * aplicação deslocar os ids de todos os mobs seguintes.
   */
  readonly nextPoisonId: number;
  /**
   * As marcas de sangramento ativas, em ordem de criação (gate V28G.N).
   *
   * Uma lista à parte dos mobs e do boss pela mesma razão que os stacks são uma:
   * pendurá-las na entidade obrigaria cada mob de cada vaga a carregar um array
   * vazio, mudando o digest canónico de toda a corrida para descrever a coisa
   * mais rara que existe.
   *
   * **Sem teto de quantidade.** O contrato é explícito, e um limite aqui seria
   * uma decisão de equilíbrio tomada por um validador.
   */
  readonly bleeding: readonly HuntSnapshotBleedingMark[];
  /**
   * A identidade da próxima marca (gate V28G.N).
   *
   * Um contador próprio, ao lado de `nextEntityId` e de `nextPoisonId` e nunca
   * partilhado com nenhum dos dois: uma marca não é uma entidade da arena, e
   * partilhar o primeiro faria cada aplicação deslocar os ids de todos os mobs
   * seguintes.
   */
  readonly nextBleedingId: number;
  /**
   * **A FASE da órbita das serras** (gate V28G.P).
   *
   * Um inteiro em `[0, SAW_ORBIT_PHASE_UNITS)`, e zero para toda a corrida sem
   * o Diamante Serrilhado.
   *
   * Estado que decide o FUTURO - onde as lâminas estão agora decide quem elas
   * tocam a seguir - e não é derivável do tique: o nível PODE mudar a meio de
   * uma sessão, porque uma queda ou uma fusão a reconfiguram no lugar, e uma
   * fase recalculada da velocidade nova teletransportaria as lâminas.
   *
   * A CENA lê-a para desenhar. É o único sítio de onde a posição de uma lâmina
   * pode vir: um relógio local daria à arena uma órbita que a simulação não tem.
   */
  readonly sawPhase: number;
  /**
   * O resto INTEIRO do orçamento das serras, string decimal (gate V28G.P).
   *
   * Estado que decide o FUTURO: sem ele, a mesma fração perdia-se em todos os
   * contactos - a truncagem é a mesma sempre, portanto não se cancela, acumula -
   * e a taxa de longo prazo ficaria abaixo do que o orçamento promete.
   */
  readonly sawCarryMilli: string;
  /**
   * Os relógios de contacto das serras, em ORDEM CANÓNICA (gate V28G.P).
   *
   * Ordenados por `(lâmina, alvo)` na escrita, para uma corrida ter uma
   * representação só: a lista viva cresce por primeiro contacto, que depende de
   * onde os mobs desceram.
   *
   * Sem eles, uma retoma daria a todas as lâminas um contacto imediato - um par
   * que nunca se tocou está sempre pronto - e o primeiro tique depois de um
   * checkpoint pagaria um intervalo inteiro de dano de uma vez.
   */
  readonly saws: readonly HuntSnapshotSawContact[];
  /**
   * The active pet, or null.
   *
   * In the snapshot because every field of it decides the FUTURE: the drift
   * phase places the next volley's muzzles, and the firing accumulator decides
   * when that volley happens. Restoring without them would reset the pet's
   * cadence on every checkpoint and quietly change what it contributed.
   */
  readonly pet: PetEntity | null;
  /**
   * **As invocações a existir no campo** (versão 14, gate V28H.M).
   *
   * O Drone e o Leviatã. Elas têm posição, vida e relógio seus, portanto uma
   * corrida retomada sem elas perderia entidades vivas - e um Leviatã com a
   * nave lá dentro perderia a nave lá dentro.
   *
   * Opcional: um documento da versão 13 descreve uma corrida em que nenhuma
   * podia existir, e a lista vazia é o estado exacto dela.
   */
  readonly summons?: readonly HuntSummonEntity[];
  /**
   * **As três cartas desta corrida** (gate V28H.H).
   *
   * Entre zero e três entradas, pela ordem canónica dos espaços. Vazia para uma
   * conta sem cartas equipadas, e vazia é o estado normal.
   *
   * Cada campo decide o futuro - ver a nota da versão 13 acima - e nenhum é
   * visual: a posição dos dois canhões temporários é derivada da nave a cada
   * leitura, e portanto não vive aqui.
   */
  readonly cards: readonly CardAbilityState[];
  readonly progression: HuntSnapshotProgression;
  readonly earned: HuntSnapshotEarned;
  readonly metrics: {
    readonly damageEvents: readonly HuntSnapshotTimedAmount[];
    readonly goldEvents: readonly HuntSnapshotTimedAmount[];
    readonly firstDamageAtMs: number | null;
    readonly firstGoldAtMs: number | null;
  };
  readonly run: {
    readonly kills: number;
    readonly damage: number;
    readonly gold: string;
  };
  readonly deathCause: 'boss-breach' | 'mob-leaks' | null;
  readonly death: {
    readonly survivedMs: number;
    readonly kills: number;
    readonly damageDealt: number;
    readonly goldEarned: number;
  } | null;
  /** Presentation events not yet drained. Kept so a restore loses nothing. */
  readonly events: readonly HuntEvent[];
}

export class InvalidHuntSnapshotError extends Error {
  constructor(detail: string) {
    // The detail names the FIELD, never echoes the value: a snapshot may carry
    // player data and error strings end up in logs.
    super(`invalid hunt snapshot: ${detail}`);
    this.name = 'InvalidHuntSnapshotError';
  }
}

function fail(detail: string): never {
  throw new InvalidHuntSnapshotError(detail);
}

/* -------------------------------------------------------------------------- */
/*  Field validators                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Defensive ceiling on a decimal field. Not a level cap - a payload guard, so
 * a hostile snapshot cannot hand the parser a megabyte-long integer.
 */
export const MAX_DECIMAL_DIGITS = 256;

const DECIMAL_PATTERN = /^(0|[1-9][0-9]*)$/;

/**
 * A non-negative integer written in plain decimal.
 *
 * Rejects a leading `+`, a leading zero, whitespace, a decimal point and
 * scientific notation: every one of those is a different value from what it
 * looks like, and accepting them would make the digest ambiguous.
 */
export function parseDecimalString(value: unknown, field: string): bigint {
  if (typeof value !== 'string') {
    fail(`${field} must be a decimal string`);
  }
  if (value.length === 0 || value.length > MAX_DECIMAL_DIGITS) {
    fail(`${field} has an unacceptable length`);
  }
  if (!DECIMAL_PATTERN.test(value)) {
    fail(`${field} is not a canonical non-negative decimal`);
  }
  return BigInt(value);
}

/**
 * Um decimal canónico que fica STRING.
 *
 * `parseDecimalString` valida e devolve um `bigint`, que é o que a progressão
 * quer. Um projétil quer o contrário: a instantânea guarda a string, e é a
 * string que atravessa o digest. Validar e devolver o original é o que mantém a
 * serialização canónica byte a byte igual dos dois lados.
 */
function decimalField(value: unknown, field: string): string {
  parseDecimalString(value, field);
  return value as string;
}

function record(value: unknown, field: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    fail(`${field} must be an object`);
  }
  return value as Record<string, unknown>;
}

function finiteNumber(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    fail(`${field} must be a finite number`);
  }
  return value;
}

function nonNegative(value: unknown, field: string): number {
  const parsed = finiteNumber(value, field);
  if (parsed < 0) {
    fail(`${field} must not be negative`);
  }
  return parsed;
}

function integer(value: unknown, field: string): number {
  const parsed = finiteNumber(value, field);
  if (!Number.isSafeInteger(parsed)) {
    fail(`${field} must be a safe integer`);
  }
  return parsed;
}

function nonNegativeInteger(value: unknown, field: string): number {
  const parsed = integer(value, field);
  if (parsed < 0) {
    fail(`${field} must not be negative`);
  }
  return parsed;
}

/**
 * O resto sub-milli do Roubo de Vida: inteiro e estritamente abaixo do módulo.
 *
 * O limite vem do resolvedor e não é escrito à mão aqui, pela mesma disciplina
 * que faz o denominador da loteria ser derivado: mudar o módulo num sítio não
 * pode deixar um leitor a aceitar valores que a aritmética já não produz.
 */
function boundedCarry(value: unknown, field: string): number {
  const parsed = nonNegativeInteger(value, field);
  if (parsed >= LIFESTEAL_CARRY_MODULUS) {
    fail(`${field} must be below ${LIFESTEAL_CARRY_MODULUS}`);
  }
  return parsed;
}

/**
 * A fase de uma órbita: inteira e estritamente abaixo do módulo da volta.
 *
 * O limite vem de `damage/saw.ts` e não é escrito à mão aqui, pela mesma
 * disciplina de {@link boundedCarry}: mudar o período da órbita num sítio não
 * pode deixar um leitor a aceitar ângulos que a aritmética já não produz.
 */
function boundedPhase(value: unknown, field: string): number {
  const parsed = nonNegativeInteger(value, field);
  if (parsed >= SAW_ORBIT_PHASE_UNITS) {
    fail(`${field} must be below ${SAW_ORBIT_PHASE_UNITS}`);
  }
  return parsed;
}

function boolean(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') {
    fail(`${field} must be a boolean`);
  }
  return value;
}

function array(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) {
    fail(`${field} must be an array`);
  }
  return value;
}

/**
 * O proc congelado de um projétil, ou `null` para um tiro comum.
 *
 * O id é validado contra o REGISTO da loteria e nunca contra um literal escrito
 * aqui: este ficheiro não sabe - e não pode saber - que joias existem. A
 * entropia é um inteiro sem sinal de 32 bits, exatamente como nasceu.
 */
/**
 * A bomba congelada de um projétil, ou `undefined` para um tiro comum.
 *
 * Descodificador exato, como o do proc e pela mesma razão: o que este campo
 * decide é dano em área, e um objeto aberto seria um documento malformado a
 * entrar na simulação.
 */
function atomicBombSelection(value: unknown, field: string): AtomicBombSelectionWire | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const source = record(value, field);
  const entropy = nonNegativeInteger(source.entropy, `${field}.entropy`);
  if (entropy > 0xffff_ffff) {
    fail(`${field}.entropy must be a uint32`);
  }
  const blastMilli = decimalField(source.blastMilli, `${field}.blastMilli`);
  if (blastMilli.startsWith('-')) {
    fail(`${field}.blastMilli must not be negative`);
  }
  return { entropy, blastMilli };
}

function jewelProcSelection(value: unknown, field: string): JewelProcSelectionWire | null {
  if (value === null || value === undefined) {
    /*
     * `undefined` é `null`, e a razão é a versão. Um documento da versão 5 tem
     * sempre o campo; o que chega sem ele é um objeto construído à mão num
     * teste, e um tiro sem proc é o estado normal.
     */
    return null;
  }
  const source = record(value, field);
  const id = source.id;
  if (typeof id !== 'string' || !isOutgoingProcId(id)) {
    fail(`${field}.id is not a registered proc`);
  }
  const entropy = nonNegativeInteger(source.entropy, `${field}.entropy`);
  if (entropy > 0xffff_ffff) {
    fail(`${field}.entropy must be a uint32`);
  }
  /*
   * A magnitude CONGELADA, validada como o decimal que é e nunca recalculada.
   *
   * Um documento que não a trouxesse não teria como a reconstruir: o perfil da
   * corrida pode ter mudado desde o disparo, e é exatamente por isso que ela
   * viaja. `decimalField` recusa qualquer coisa que não seja uma string de
   * dígitos, portanto um `number` de JSON não passa aqui.
   */
  const cutMilli = decimalField(source.cutMilli, `${field}.cutMilli`);
  if (cutMilli.startsWith('-')) {
    fail(`${field}.cutMilli must not be negative`);
  }
  return { id, entropy, cutMilli };
}

function rngState(value: unknown, field: string): SimulationRngState {
  const source = record(value, field);
  const seed = source.seed;
  const state = source.state;
  if (typeof seed !== 'string' || !/^\d{1,20}$/.test(seed)) {
    fail(`${field}.seed must be a decimal string`);
  }
  if (typeof state !== 'string' || !/^\d{1,20}$/.test(state)) {
    fail(`${field}.state must be a decimal string`);
  }
  return { seed, state, drawCount: nonNegativeInteger(source.drawCount, `${field}.drawCount`) };
}

function mobEntity(value: unknown, index: number): MobEntity {
  const source = record(value, `mobs[${index}]`);
  const maxHealth = nonNegative(source.maxHealth, `mobs[${index}].maxHealth`);
  const health = nonNegative(source.health, `mobs[${index}].health`);
  if (health > maxHealth) {
    fail(`mobs[${index}].health exceeds maxHealth`);
  }
  return {
    id: nonNegativeInteger(source.id, `mobs[${index}].id`),
    kind: 'mob',
    x: finiteNumber(source.x, `mobs[${index}].x`),
    y: finiteNumber(source.y, `mobs[${index}].y`),
    health,
    maxHealth,
    size: nonNegative(source.size, `mobs[${index}].size`),
    leaked: boolean(source.leaked, `mobs[${index}].leaked`),
  };
}

/**
 * The bound encounter, or null.
 *
 * Absent in every snapshot written before encounters existed. Null is the
 * honest answer for those: the server adopts them at the next bootstrap and
 * writes a real encounter, which is a decision that belongs to the server and
 * not to a parser inventing a tier.
 */
function bossEncounterState(value: unknown): HuntSnapshotBossEncounter | null {
  if (value === null || value === undefined) {
    return null;
  }
  const source = record(value, 'bossEncounter');
  // Null is a legitimate answer, not a missing field: it is what a browser
  // holds for an encounter whose boss has not arrived yet.
  const tier = source.tier === null ? null : nonNegativeInteger(source.tier, 'bossEncounter.tier');
  if (tier !== null && (tier < 1 || tier > 6)) {
    fail('bossEncounter.tier is not a tier');
  }
  if (typeof source.encounterId !== 'string' || source.encounterId.length === 0) {
    fail('bossEncounter.encounterId is missing');
  }
  if (typeof source.activated !== 'boolean') {
    fail('bossEncounter.activated is not a boolean');
  }
  return {
    encounterId: source.encounterId,
    ordinal: nonNegativeInteger(source.ordinal, 'bossEncounter.ordinal'),
    tier,
    activated: source.activated,
    // Absent before recovery existed, and false is what those runs were.
    retrying: source.retrying === true,
  };
}

function bossEntity(value: unknown): BossEntity | null {
  if (value === null || value === undefined) {
    return null;
  }
  const source = record(value, 'boss');
  const phase = source.phase;
  if (phase !== 'revealing' && phase !== 'descending') {
    fail('boss.phase is unknown');
  }
  const maxHealth = nonNegative(source.maxHealth, 'boss.maxHealth');
  const health = nonNegative(source.health, 'boss.health');
  if (health > maxHealth) {
    fail('boss.health exceeds maxHealth');
  }
  return {
    id: nonNegativeInteger(source.id, 'boss.id'),
    kind: 'boss',
    x: finiteNumber(source.x, 'boss.x'),
    y: finiteNumber(source.y, 'boss.y'),
    health,
    maxHealth,
    scale: nonNegative(source.scale, 'boss.scale'),
    size: nonNegative(source.size, 'boss.size'),
    phase,
    revealRemainingMs: finiteNumber(source.revealRemainingMs, 'boss.revealRemainingMs'),
  };
}

/**
 * Um míssil, validado campo a campo (versão 8).
 *
 * Muito mais curto do que o de um projétil, e isso é o ponto: um míssil tem
 * exatamente o que o contrato manda sobreviver e mais nada. Sem `orphaned`, sem
 * proc, sem bomba, sem raio de explosão e sem `detonates` - nenhum desses
 * conceitos existe nesta entidade, e um campo a mais aqui seria um campo que a
 * simulação teria de decidir o que fazer com.
 *
 * `targetId` aceita `null` porque a simulação pode tocar esse estado: um míssil
 * que perdeu o alvo tem `null` até resolver, e recusá-lo obrigaria o codificador
 * a inventar um alvo. O que ele não faz é sobreviver a um tique - um míssil sem
 * alvo explode.
 */
/**
 * Um stack de veneno, validado campo a campo (versão 9).
 *
 * Cinco campos, e são os que o contrato manda sobreviver: identidade, alvo,
 * total congelado, instante de aplicação e parcelas já pagas. Não há sexto: o
 * próximo tick e as parcelas restantes são funções exatas dos dois últimos, e
 * guardá-los seria guardar o mesmo facto três vezes.
 *
 * `ticksDone` é LIMITADO a seis, e a recusa é a mesma disciplina de
 * `lifestealCarryBps`: um sete descreveria uma sétima parcela que não existe,
 * portanto é um documento que a simulação não escreveu.
 */
function poisonStack(value: unknown, index: number): HuntSnapshotPoisonStack {
  const source = record(value, `poison[${index}]`);
  const ticksDone = nonNegativeInteger(source.ticksDone, `poison[${index}].ticksDone`);
  if (ticksDone > POISON_TICK_COUNT) {
    fail(`poison[${index}].ticksDone is above ${POISON_TICK_COUNT}`);
  }
  return {
    id: nonNegativeInteger(source.id, `poison[${index}].id`),
    targetId: integer(source.targetId, `poison[${index}].targetId`),
    totalDamageMilli: decimalField(source.totalDamageMilli, `poison[${index}].totalDamageMilli`),
    appliedAt: nonNegative(source.appliedAt, `poison[${index}].appliedAt`),
    ticksDone,
  };
}

/**
 * Uma marca de sangramento, decodificada (gate V28G.N).
 *
 * Quatro campos, e nenhuma verificação de prazo: uma marca não expira, portanto
 * não há contador para validar e não há forma de um documento descrever uma
 * marca vencida. O que a acaba é uma ordem, e uma ordem não está aqui.
 */
function bleedingMark(value: unknown, index: number): HuntSnapshotBleedingMark {
  const source = record(value, `bleeding[${index}]`);
  return {
    id: nonNegativeInteger(source.id, `bleeding[${index}].id`),
    targetId: integer(source.targetId, `bleeding[${index}].targetId`),
    damageMilli: decimalField(source.damageMilli, `bleeding[${index}].damageMilli`),
    appliedAt: nonNegative(source.appliedAt, `bleeding[${index}].appliedAt`),
  };
}

/**
 * Um relógio de contacto de serra, decodificado (gate V28G.P).
 *
 * Três campos. O índice da lâmina é limitado a `[0, SAW_MAX_BLADES)` porque
 * quatro é o máximo que o nível 30 mantém - uma quinta descreveria uma órbita
 * que nenhum nível produz - e o instante é limitado a não-negativo pela mesma
 * razão que `appliedAt` é: o relógio da corrida começa em zero.
 */
function sawContact(value: unknown, index: number): HuntSnapshotSawContact {
  const source = record(value, `saws[${index}]`);
  const bladeIndex = nonNegativeInteger(source.bladeIndex, `saws[${index}].bladeIndex`);
  if (bladeIndex >= SAW_MAX_BLADES) {
    fail(`saws[${index}].bladeIndex is ${bladeIndex}: an orbit holds at most ${SAW_MAX_BLADES}`);
  }
  return {
    bladeIndex,
    targetId: integer(source.targetId, `saws[${index}].targetId`),
    lastContactMs: nonNegative(source.lastContactMs, `saws[${index}].lastContactMs`),
  };
}

function missileEntity(value: unknown, index: number): HuntSnapshotMissile {
  const source = record(value, `missiles[${index}]`);
  const targetId = source.targetId;
  return {
    id: nonNegativeInteger(source.id, `missiles[${index}].id`),
    x: finiteNumber(source.x, `missiles[${index}].x`),
    y: finiteNumber(source.y, `missiles[${index}].y`),
    dirX: finiteNumber(source.dirX, `missiles[${index}].dirX`),
    dirY: finiteNumber(source.dirY, `missiles[${index}].dirY`),
    damageMilli: decimalField(source.damageMilli, `missiles[${index}].damageMilli`),
    entropy: nonNegativeInteger(source.entropy, `missiles[${index}].entropy`),
    targetId: targetId === null ? null : integer(targetId, `missiles[${index}].targetId`),
    remainingMs: finiteNumber(source.remainingMs, `missiles[${index}].remainingMs`),
  };
}

function projectileEntity(value: unknown, index: number): HuntSnapshotProjectile {
  const source = record(value, `projectiles[${index}]`);
  const projectile: HuntSnapshotProjectile = {
    id: nonNegativeInteger(source.id, `projectiles[${index}].id`),
    x: finiteNumber(source.x, `projectiles[${index}].x`),
    y: finiteNumber(source.y, `projectiles[${index}].y`),
    dirX: finiteNumber(source.dirX, `projectiles[${index}].dirX`),
    dirY: finiteNumber(source.dirY, `projectiles[${index}].dirY`),
    damageMilli: decimalField(source.damageMilli, `projectiles[${index}].damageMilli`),
    targetId: integer(source.targetId, `projectiles[${index}].targetId`),
    orphaned: boolean(source.orphaned, `projectiles[${index}].orphaned`),
    jewelProc: jewelProcSelection(source.jewelProc, `projectiles[${index}].jewelProc`),
    /*
     * A bomba, restaurada EXATAMENTE (gate V28G.J).
     *
     * É simulação e não decoração: um projétil restaurado sem ela voa a outra
     * velocidade, persegue, e bate em vez de detonar - portanto a re-simulação
     * do servidor mataria outro conjunto de mobs. Ausente em todo o tiro comum,
     * e por isso espalhada em vez de escrita como `undefined`, que mudaria o
     * digest canónico de toda a corrida.
     */
    ...(() => {
      const bomb = atomicBombSelection(source.atomicBomb, `projectiles[${index}].atomicBomb`);
      return bomb === undefined ? {} : { atomicBomb: bomb };
    })(),
  };
  // Only present on a pet bullet, and omitted rather than written as `false`
  // so the canonical digest of a run without a pet is byte-identical to the
  // one it produced before pets existed.
  const petBullet =
    source.fromPet === true ? { ...projectile, fromPet: true as const } : projectile;
  /*
   * **A BOMBA sai por aqui, com a sua velocidade e o seu fim de vida.**
   *
   * Antes do ramo explosivo, e não dentro dele, porque ela não é explosiva no
   * sentido que aquele ramo usa: ela não tem `detonates`, não tem `blastRadius`
   * e não tem `splashDamageMilli` - o raio e a magnitude dela vivem noutro sítio
   * e são outra coisa.
   *
   * Os dois campos são SIMULAÇÃO. Uma bomba restaurada sem `speed` voaria à
   * velocidade de um tiro comum, e sem `remainingMs` nunca chegaria ao fim de
   * vida - portanto a re-simulação do servidor detonaria noutro sítio e mataria
   * outro conjunto de mobs.
   */
  if (petBullet.atomicBomb !== undefined) {
    return {
      ...petBullet,
      speed: nonNegative(source.speed, `projectiles[${index}].speed`),
      remainingMs: finiteNumber(source.remainingMs, `projectiles[${index}].remainingMs`),
    };
  }
  /*
   * The blast, restored EXACTLY.
   *
   * These decide who takes damage, so they are simulation state and not
   * decoration: a ball restored without its radius would land as a single hit
   * and the server's re-simulation would disagree with the client about which
   * mobs died. Absent on every non-explosive shot, and therefore on every
   * snapshot written before the Abyssal Drone existed.
   */
  /*
   * `detonates` was derived from the radius before missiles existed.
   *
   * A snapshot written then carries a blast radius and no flag, and it meant
   * exactly "this explodes" - so it is restored as such. A missile is the other
   * case: it detonates and has no radius at all.
   */
  const detonates =
    source.detonates === true ||
    (source.detonates === undefined && typeof source.blastRadius === 'number');
  if (!detonates) {
    return petBullet;
  }
  const explosive: HuntSnapshotProjectile = {
    ...petBullet,
    detonates: true,
    fuseX: finiteNumber(source.fuseX, `projectiles[${index}].fuseX`),
    fuseY: finiteNumber(source.fuseY, `projectiles[${index}].fuseY`),
    ...(source.blastRadius === undefined
      ? {}
      : {
          blastRadius: nonNegative(source.blastRadius, `projectiles[${index}].blastRadius`),
          splashDamageMilli: decimalField(
            source.splashDamageMilli,
            `projectiles[${index}].splashDamageMilli`,
          ),
        }),
  };
  if (source.remainingMs === undefined) {
    return explosive;
  }
  /*
   * A missile mid-flight, restored exactly.
   *
   * Speed, boost and remaining life decide where it is next tick and whether it
   * expires - simulation state, so the server's re-simulation must resume it at
   * the same point rather than at full speed with a fresh fuse.
   */
  return {
    ...explosive,
    speed: nonNegative(source.speed, `projectiles[${index}].speed`),
    maxSpeed: nonNegative(source.maxSpeed, `projectiles[${index}].maxSpeed`),
    acceleration: nonNegative(source.acceleration, `projectiles[${index}].acceleration`),
    maxTurn: nonNegative(source.maxTurn, `projectiles[${index}].maxTurn`),
    remainingMs: finiteNumber(source.remainingMs, `projectiles[${index}].remainingMs`),
  };
}

/**
 * The pet, or null.
 *
 * Absent in every snapshot written before pets existed. Those runs had no pet
 * and re-simulate with none, which is exactly what they were played with.
 */
/**
 * As cartas de um documento (gate V28H.H).
 *
 * Estrito: o espaço tem de ser um dos três, os quatro relógios têm de ser
 * inteiros não negativos, e o nível tem de caber em 1..6. Um documento que não
 * satisfaça isto é RECUSADO - a versão 13 é o que garante que todo o documento
 * que chega aqui foi escrito por um build que tem cartas.
 *
 * Sem tecto de quantidade escrito à mão: três espaços é o que `CARD_SLOTS` diz,
 * e é de lá que o limite vem.
 */
function cardAbilities(value: unknown, field: string): CardAbilityState[] {
  return array(value, field).map((entry, index) => {
    const raw = record(entry, `${field}[${index}]`);
    const slot = raw.slot;
    if (typeof slot !== 'string' || !isCardSlot(slot)) {
      fail(`${field}[${index}].slot is not a card slot`);
    }
    const definitionId = raw.definitionId;
    if (typeof definitionId !== 'string' || definitionId.length === 0) {
      fail(`${field}[${index}].definitionId is empty`);
    }
    const instanceId = raw.instanceId;
    if (typeof instanceId !== 'string' || instanceId.length === 0) {
      fail(`${field}[${index}].instanceId is empty`);
    }
    const level = nonNegativeInteger(raw.level, `${field}[${index}].level`);
    if (level < 1 || level > CARD_MAX_LEVEL) {
      fail(`${field}[${index}].level is outside 1..${CARD_MAX_LEVEL}`);
    }
    return {
      slot: slot as CardAbilityState['slot'],
      definitionId,
      instanceId,
      level,
      cooldownRemainingMs: nonNegative(
        raw.cooldownRemainingMs,
        `${field}[${index}].cooldownRemainingMs`,
      ),
      activeRemainingMs: nonNegative(raw.activeRemainingMs, `${field}[${index}].activeRemainingMs`),
      attackAccumulatorMs: nonNegative(
        raw.attackAccumulatorMs,
        `${field}[${index}].attackAccumulatorMs`,
      ),
      /*
       * A CARGA (gate V28H.L), e a ausência dela é ZERO em vez de uma recusa.
       *
       * Um documento escrito antes das cartas que carregam descreve uma sessão
       * em que nenhuma podia estar a carregar - as habilidades não existiam.
       * Zero é o estado exacto dessa sessão, e não um recuo.
       */
      chargeRemainingMs:
        raw.chargeRemainingMs === undefined
          ? 0
          : nonNegative(raw.chargeRemainingMs, `${field}[${index}].chargeRemainingMs`),
      /*
       * **A FORÇA da carga** (gate V28H.N), e a ausência dela é CHEIA.
       *
       * Um documento escrito antes de a carga ser graduada descreve um feixe
       * que só sabia arder com tudo o que tinha - era literalmente um feixe
       * cheio. Zero seria reescrever a história ao contrário: um feixe que
       * existiu a arder passaria a não cobrar nada.
       */
      chargeStrengthBps:
        raw.chargeStrengthBps === undefined
          ? CARD_CHARGE_FULL_BPS
          : nonNegative(raw.chargeStrengthBps, `${field}[${index}].chargeStrengthBps`),
    };
  });
}

/**
 * **As bombas de carta em voo, lidas do documento** (gate V28H.T).
 *
 * Cada campo é exigido: uma bomba sem alvo, sem raio ou sem dano não é uma
 * bomba a meio caminho - é um documento que descreve outra coisa, e adivinhar o
 * que falta seria inventar uma explosão.
 *
 * A ausência da LISTA é a lista vazia, que é diferente: um documento anterior a
 * este gate descreve uma corrida em que nenhuma podia existir.
 */
function cardBlasts(value: unknown): HuntSnapshot['cardBlasts'][number][] {
  if (value === undefined || value === null) {
    return [];
  }
  if (!Array.isArray(value)) {
    fail('cardBlasts is not a list');
  }
  return (value as unknown[]).map((raw, index) => {
    const where = `cardBlasts[${index}]`;
    if (typeof raw !== 'object' || raw === null) {
      fail(`${where} is not an object`);
    }
    /*
     * `blast` e nao `entry`: `entry` e o vocabulario do PERFIL DE JOIAS, e um
     * guarda proibe o literal fora dos dois ficheiros que podem le-lo. Uma bomba
     * de carta nao tem nada a ver com uma joia, e o nome tem de o dizer.
     */
    const blast = raw as Record<string, unknown>;
    const kind = blast.kind;
    if (kind !== 'fireball' && kind !== 'catole') {
      fail(`${where}.kind is ${String(kind)}`);
    }
    return {
      id: nonNegativeInteger(blast.id, `${where}.id`),
      kind,
      x: finiteNumber(blast.x, `${where}.x`),
      y: finiteNumber(blast.y, `${where}.y`),
      targetId: nonNegativeInteger(blast.targetId, `${where}.targetId`),
      speed: nonNegative(blast.speed, `${where}.speed`),
      radius: nonNegative(blast.radius, `${where}.radius`),
      /*
       * O dano em MILLI, como string decimal e validado como tal: um número
       * aqui perderia dígitos que a economia deste jogo tem, e o formato inteiro
       * já fala em strings por essa razão.
       */
      damageMilli: decimalField(blast.damageMilli, `${where}.damageMilli`),
      ageMs: nonNegative(blast.ageMs, `${where}.ageMs`),
    };
  });
}

/**
 * **As oito ranhuras de efeito, lidas do documento** (gate V28H.T).
 *
 * Sempre oito, pela ordem de `CARD_EFFECT_SLOTS`. Uma lista mais curta é
 * completada com ranhuras vazias em vez de recusada: um documento anterior a
 * este gate não as tem, e vazio é o estado exacto da corrida que ele descreve.
 *
 * Uma lista MAIS LONGA é recusada - ela descreve um mundo com mais efeitos do
 * que este build conhece, e adivinhar quais ignorar seria adivinhar regras.
 */
function cardEffectSlots(value: unknown): { ms: number; bps: number }[] {
  if (value === undefined || value === null) {
    return CARD_EFFECT_SLOTS.map(() => ({ ms: 0, bps: 0 }));
  }
  if (!Array.isArray(value)) {
    fail('cardEffects is not a list');
  }
  const list = value as unknown[];
  if (list.length > CARD_EFFECT_SLOTS.length) {
    fail(`cardEffects has ${list.length} slots, and this build knows ${CARD_EFFECT_SLOTS.length}`);
  }
  return CARD_EFFECT_SLOTS.map((slot, index) => {
    const raw = list[index];
    if (raw === undefined) {
      return { ms: 0, bps: 0 };
    }
    /*
     * **A invulnerabilidade não atravessa** (gate V28I.C).
     *
     * O Bastião passou a ser somente do duelo, e esta ranhura só podia ter sido
     * escrita nesta arena por ele. Um documento da versão anterior pode trazê-la
     * a correr; zerá-la aqui, no LEITOR, é o que garante que ela não ressuscita
     * numa retoma nem numa re-simulação do servidor.
     *
     * No leitor e não em quem restaura: este é o sítio por onde todo o documento
     * entra, e um segundo sítio seria um sítio que se esquece.
     */
    if (slot === 'untargetable') {
      return { ms: 0, bps: 0 };
    }
    if (typeof raw !== 'object' || raw === null) {
      fail(`cardEffects[${index}] (${slot}) is not an object`);
    }
    const entry = raw as { ms?: unknown; bps?: unknown };
    return {
      ms: nonNegative(entry.ms, `cardEffects[${index}].ms`),
      bps: nonNegative(entry.bps, `cardEffects[${index}].bps`),
    };
  });
}

/**
 * As invocações, uma a uma, com os campos exactos.
 *
 * A ausência do campo é a LISTA VAZIA e não uma recusa: um documento da versão
 * 13 descreve uma corrida em que nenhuma invocação podia existir.
 */
function summonEntities(value: unknown): HuntSummonEntity[] {
  if (value === undefined || value === null) {
    return [];
  }
  return array(value, 'summons').map((entry, index) => {
    const raw = record(entry, `summons[${index}]`);
    const kind = raw.kind;
    if (kind !== 'drone' && kind !== 'leviathan') {
      fail(`summons[${index}].kind is not a summon kind`);
    }
    return {
      id: nonNegativeInteger(raw.id, `summons[${index}].id`),
      kind,
      x: finiteNumber(raw.x, `summons[${index}].x`),
      y: finiteNumber(raw.y, `summons[${index}].y`),
      facing: finiteNumber(raw.facing, `summons[${index}].facing`),
      healthMilli: nonNegative(raw.healthMilli, `summons[${index}].healthMilli`),
      maxHealthMilli: nonNegative(raw.maxHealthMilli, `summons[${index}].maxHealthMilli`),
      lifeMs: nonNegative(raw.lifeMs, `summons[${index}].lifeMs`),
      attackAccumulatorMs: nonNegative(
        raw.attackAccumulatorMs,
        `summons[${index}].attackAccumulatorMs`,
      ),
      boarded: raw.boarded === true,
      boardedRemainingMs: nonNegative(
        raw.boardedRemainingMs,
        `summons[${index}].boardedRemainingMs`,
      ),
    };
  });
}

function petEntity(value: unknown): PetEntity | null {
  if (value === null || value === undefined) {
    return null;
  }
  const source = record(value, 'pet');
  const definitionId = source.definitionId;
  if (typeof definitionId !== 'string' || definitionId.length === 0) {
    fail('pet.definitionId must be a non-empty string');
  }
  return {
    definitionId,
    evolution: nonNegativeInteger(source.evolution, 'pet.evolution'),
    // Tolerant of absence, and only of absence: a snapshot written before
    // training existed describes an untrained pet, which is exactly right.
    // A present-but-malformed value still fails loudly.
    damageFoodCount:
      source.damageFoodCount === undefined
        ? 0
        : nonNegativeInteger(source.damageFoodCount, 'pet.damageFoodCount'),
    attackSpeedFoodCount:
      source.attackSpeedFoodCount === undefined
        ? 0
        : nonNegativeInteger(source.attackSpeedFoodCount, 'pet.attackSpeedFoodCount'),
    x: finiteNumber(source.x, 'pet.x'),
    y: finiteNumber(source.y, 'pet.y'),
    facing: finiteNumber(source.facing, 'pet.facing'),
    driftSeconds: nonNegative(source.driftSeconds, 'pet.driftSeconds'),
    attackAccumulatorMs: nonNegative(source.attackAccumulatorMs, 'pet.attackAccumulatorMs'),
    /*
     * The beam's authoritative state.
     *
     * Absent in every snapshot written before the Void Butterfly existed, and
     * those runs had no beam - restoring them with the beam off is exactly what
     * they were played with. A PRESENT value is still validated: a lock the
     * server cannot reproduce would silently move damage to another enemy.
     */
    beamTargetId:
      source.beamTargetId === undefined || source.beamTargetId === null
        ? null
        : nonNegativeInteger(source.beamTargetId, 'pet.beamTargetId'),
    beamAccumulatorMs:
      source.beamAccumulatorMs === undefined
        ? 0
        : nonNegative(source.beamAccumulatorMs, 'pet.beamAccumulatorMs'),
    beamReacquireMs:
      source.beamReacquireMs === undefined
        ? 0
        : nonNegative(source.beamReacquireMs, 'pet.beamReacquireMs'),
    damageDealt: nonNegative(source.damageDealt, 'pet.damageDealt'),
  };
}

function timedAmounts(value: unknown, field: string): HuntSnapshotTimedAmount[] {
  return array(value, field).map((entry, index) => {
    const source = record(entry, `${field}[${index}]`);
    return {
      t: nonNegative(source.t, `${field}[${index}].t`),
      amount: nonNegative(source.amount, `${field}[${index}].amount`),
    };
  });
}

function nullableNonNegative(value: unknown, field: string): number | null {
  return value === null || value === undefined ? null : nonNegative(value, field);
}

/* -------------------------------------------------------------------------- */
/*  Progression conversion                                                    */
/* -------------------------------------------------------------------------- */

/** Snapshot progression -> the domain state, with the invariants enforced. */
export function toProgressionState(progression: HuntSnapshotProgression): ProgressionState {
  const state: ProgressionState = {
    level: parseDecimalString(progression.level, 'progression.level'),
    xpIntoLevel: parseDecimalString(progression.xpIntoLevel, 'progression.xpIntoLevel'),
    lifetimeXp: parseDecimalString(progression.lifetimeXp, 'progression.lifetimeXp'),
    freePoints: parseDecimalString(progression.freePoints, 'progression.freePoints'),
    damagePoints: parseDecimalString(progression.damagePoints, 'progression.damagePoints'),
    healthPoints: parseDecimalString(progression.healthPoints, 'progression.healthPoints'),
    attackSpeedPoints: parseDecimalString(
      progression.attackSpeedPoints,
      'progression.attackSpeedPoints',
    ),
  };
  try {
    // level/xpIntoLevel/lifetimeXp must agree; a snapshot that disagrees is a
    // corrupted or forged one, not something to "best effort" continue.
    assertProgression(state);
  } catch (error) {
    fail(`progression is inconsistent (${(error as Error).name})`);
  }
  return state;
}

/** Domain state + session Gold -> the snapshot's decimal form. */
export function fromProgressionState(
  state: ProgressionState,
  gold: bigint,
): HuntSnapshotProgression {
  return {
    level: state.level.toString(10),
    xpIntoLevel: state.xpIntoLevel.toString(10),
    lifetimeXp: state.lifetimeXp.toString(10),
    gold: gold.toString(10),
    freePoints: state.freePoints.toString(10),
    damagePoints: state.damagePoints.toString(10),
    healthPoints: state.healthPoints.toString(10),
    attackSpeedPoints: state.attackSpeedPoints.toString(10),
  };
}

/* -------------------------------------------------------------------------- */
/*  Parsing                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Validates untyped JSON into a snapshot, or throws.
 *
 * An unknown `version` is rejected outright rather than "best effort" parsed:
 * a build that cannot faithfully continue a run must refuse it, not guess.
 * Version 1 snapshots are deliberately NOT migrated in place - the session is
 * superseded and a fresh one is built from the confirmed progression.
 */
export function parseHuntSnapshot(raw: unknown): HuntSnapshot {
  const source = record(raw, 'snapshot');
  if (source.version !== HUNT_SNAPSHOT_VERSION) {
    fail(`version ${String(source.version)} is not supported`);
  }
  const phase = source.phase;
  if (phase !== 'active' && phase !== 'dead') {
    fail('phase is unknown');
  }
  const deathCause = source.deathCause;
  if (deathCause !== null && deathCause !== 'boss-breach' && deathCause !== 'mob-leaks') {
    fail('deathCause is unknown');
  }
  const rng = record(source.rng, 'rng');
  const progressionSource = record(source.progression, 'progression');
  const earned = record(source.earned, 'earned');
  const metrics = record(source.metrics, 'metrics');
  const run = record(source.run, 'run');
  const death =
    source.death === null || source.death === undefined ? null : record(source.death, 'death');

  const progression: HuntSnapshotProgression = {
    level: String(progressionSource.level),
    xpIntoLevel: String(progressionSource.xpIntoLevel),
    lifetimeXp: String(progressionSource.lifetimeXp),
    gold: String(progressionSource.gold),
    freePoints: String(progressionSource.freePoints),
    damagePoints: String(progressionSource.damagePoints),
    healthPoints: String(progressionSource.healthPoints),
    attackSpeedPoints: String(progressionSource.attackSpeedPoints),
  };
  // Parses every field, checks the digits AND the progression invariants.
  toProgressionState(progression);
  parseDecimalString(progression.gold, 'progression.gold');

  return {
    version: HUNT_SNAPSHOT_VERSION,
    tickMs: nonNegative(source.tickMs, 'tickMs'),
    /*
     * A ausência é ZERO, e é a verdade: um documento escrito antes de a carga
     * prender a deriva descreve uma corrida em que nada a prendeu.
     */
    driftFrozenMs:
      source.driftFrozenMs === undefined ? 0 : nonNegative(source.driftFrozenMs, 'driftFrozenMs'),
    cardEffects: cardEffectSlots(source.cardEffects),
    cardBlasts: cardBlasts(source.cardBlasts),
    pendingMs: nonNegative(source.pendingMs, 'pendingMs'),
    runStartMs: nonNegative(source.runStartMs, 'runStartMs'),
    phase,
    nextEntityId: nonNegativeInteger(source.nextEntityId, 'nextEntityId'),
    facing: finiteNumber(source.facing, 'facing'),
    health: nonNegative(source.health, 'health'),
    spawnAccumulatorMs: nonNegative(source.spawnAccumulatorMs, 'spawnAccumulatorMs'),
    // Absent before artifacts existed. Those runs had no regeneration, so
    // starting the carry at zero cannot lose a fraction that never accrued.
    regenCarryMilli: nonNegative(source.regenCarryMilli ?? 0, 'regenCarryMilli'),
    /*
     * Obrigatório e LIMITADO, ao contrário do de cima.
     *
     * Um resto igual ou maior que dez mil é um resto que nenhuma sequência de
     * golpes pode produzir - por definição de resto - portanto aceitá-lo seria
     * aceitar um documento que a simulação não escreveu. `lifestealEarnedMilli`
     * recusa-o na mesma, e recusar aqui é recusá-lo antes de ele entrar.
     */
    lifestealCarryBps: boundedCarry(source.lifestealCarryBps, 'lifestealCarryBps'),
    // Absent only in snapshots written before arrivals were scheduled. Such a
    // run simply has nothing pending until its window rolls over - at most one
    // window without enemies, and never a burst to make up for it.
    spawnSchedule: array(source.spawnSchedule ?? [], 'spawnSchedule').map((value, index) =>
      nonNegative(value, `spawnSchedule[${index}]`),
    ),
    attackAccumulatorMs: nonNegative(source.attackAccumulatorMs, 'attackAccumulatorMs'),
    bossCountdownMs: finiteNumber(source.bossCountdownMs, 'bossCountdownMs'),
    rng: {
      spawn: rngState(rng.spawn, 'rng.spawn'),
      bossTimer: rngState(rng.bossTimer, 'rng.bossTimer'),
      // Validated when present, omitted when not: a run that predates pet
      // targeting has no state to restore and is rebuilt from its own seed.
      ...(rng.petTarget === undefined
        ? {}
        : { petTarget: rngState(rng.petTarget, 'rng.petTarget') }),
      // Obrigatório desde a versão 4: todo o documento que chega aqui foi
      // escrito por um build que tem a loteria, e portanto tem o fluxo.
      jewelProc: rngState(rng.jewelProc, 'rng.jewelProc'),
      // Obrigatório desde a versão 7, pela mesma razão exata.
      volleyBomb: rngState(rng.volleyBomb, 'rng.volleyBomb'),
      // Obrigatório desde a versão 8, pela mesma razão exata (gate V28G.L).
      missileTarget: rngState(rng.missileTarget, 'rng.missileTarget'),
      // Obrigatório desde a versão 9, pela mesma razão exata (gate V28G.M).
      poison: rngState(rng.poison, 'rng.poison'),
      // Obrigatório desde a versão 10, pela mesma razão exata (gate V28G.N).
      bleeding: rngState(rng.bleeding, 'rng.bleeding'),
      // Obrigatório desde a versão 11, pela mesma razão exata (gate V28G.P).
      saw: rngState(rng.saw, 'rng.saw'),
      // Obrigatório desde a versão 12, pela mesma razão exata (gate V28G.U).
      vitalHeart: rngState(rng.vitalHeart, 'rng.vitalHeart'),
    },
    bossEncounter: bossEncounterState(source.bossEncounter),
    mobs: array(source.mobs, 'mobs').map(mobEntity),
    boss: bossEntity(source.boss),
    projectiles: array(source.projectiles, 'projectiles').map(projectileEntity),
    /* Os mísseis em voo, exigidos pela versão 8 e nunca inventados aqui. */
    missiles: array(source.missiles, 'missiles').map(missileEntity),
    /*
     * Os stacks de veneno, exigidos pela versão 9 e nunca inventados aqui.
     *
     * Sem teto de quantidade, deliberadamente: o contrato proíbe um `.max(N)`
     * arbitrário, e um limite aqui seria uma decisão de equilíbrio tomada por um
     * validador.
     */
    poison: array(source.poison, 'poison').map(poisonStack),
    nextPoisonId: nonNegativeInteger(source.nextPoisonId, 'nextPoisonId'),
    /*
     * As marcas de sangramento, exigidas pela versão 10 e nunca inventadas aqui.
     *
     * Sem teto de quantidade, deliberadamente: o contrato proíbe um limite, e um
     * aqui seria uma decisão de equilíbrio tomada por um validador.
     */
    bleeding: array(source.bleeding, 'bleeding').map(bleedingMark),
    nextBleedingId: nonNegativeInteger(source.nextBleedingId, 'nextBleedingId'),
    /*
     * A órbita, o resto e os relógios das serras, exigidos pela versão 11.
     *
     * A fase é limitada ao módulo da volta e não a um inteiro qualquer: um valor
     * fora dele não pode ter sido produzido por nenhuma sequência de tiques.
     *
     * Os relógios não têm teto de quantidade, deliberadamente: quantos pares
     * existem depende de quantos inimigos estão no campo, que o contrato também
     * não limita.
     */
    sawPhase: boundedPhase(source.sawPhase, 'sawPhase'),
    sawCarryMilli: decimalField(source.sawCarryMilli, 'sawCarryMilli'),
    saws: array(source.saws, 'saws').map(sawContact),
    pet: petEntity(source.pet),
    /* As invocações (versão 14, gate V28H.M). Ausentes = nenhuma. */
    summons: summonEntities(source.summons),
    cards: cardAbilities(source.cards, 'cards'),
    progression,
    earned: {
      xp: parseDecimalString(earned.xp, 'earned.xp').toString(10),
      gold: parseDecimalString(earned.gold, 'earned.gold').toString(10),
      damage: nonNegative(earned.damage, 'earned.damage'),
      kills: nonNegativeInteger(earned.kills, 'earned.kills'),
      // Absent only in snapshots written before normal Gold became a fraction.
      // Those sessions restart the count from zero, which costs at most the
      // two Gold of an unfinished group and never grants one that was not
      // earned; every snapshot written since carries it.
      normalKills: nonNegativeInteger(earned.normalKills ?? 0, 'earned.normalKills'),
      // Absent in snapshots written before artifacts existed. Those sessions
      // had no Gold percentage, so a boss was a whole number and starting the
      // count at zero cannot lose a fraction that was never there.
      bossKills: nonNegativeInteger(earned.bossKills ?? 0, 'earned.bossKills'),
      // Absent before tiers existed. Such a session's bosses were all worth
      // the flat 60, so the restore below rebuilds the base from the count
      // rather than losing it.
      ...(earned.bossGoldBase === undefined
        ? {}
        : {
            bossGoldBase: parseDecimalString(earned.bossGoldBase, 'earned.bossGoldBase').toString(
              10,
            ),
          }),
    },
    metrics: {
      damageEvents: timedAmounts(metrics.damageEvents, 'metrics.damageEvents'),
      goldEvents: timedAmounts(metrics.goldEvents, 'metrics.goldEvents'),
      firstDamageAtMs: nullableNonNegative(metrics.firstDamageAtMs, 'metrics.firstDamageAtMs'),
      firstGoldAtMs: nullableNonNegative(metrics.firstGoldAtMs, 'metrics.firstGoldAtMs'),
    },
    run: {
      kills: nonNegativeInteger(run.kills, 'run.kills'),
      damage: nonNegative(run.damage, 'run.damage'),
      gold: parseDecimalString(run.gold, 'run.gold').toString(10),
    },
    deathCause,
    death: death
      ? {
          survivedMs: nonNegative(death.survivedMs, 'death.survivedMs'),
          kills: nonNegativeInteger(death.kills, 'death.kills'),
          damageDealt: nonNegative(death.damageDealt, 'death.damageDealt'),
          goldEarned: nonNegative(death.goldEarned, 'death.goldEarned'),
        }
      : null,
    // Presentation events are replayed verbatim: they never influence future
    // state, so they are carried but not interpreted.
    events: array(source.events, 'events') as readonly HuntEvent[],
  };
}

/* -------------------------------------------------------------------------- */
/*  Canonical form and digest                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Deterministic JSON for a snapshot: object keys sorted, arrays in order.
 *
 * Client and server must produce byte-identical text for identical state, or
 * the divergence digest would fire on key order rather than on a real
 * difference. `JSON.stringify` alone does not promise that across engines.
 *
 * It hashes the SIMULATION, not everything the snapshot carries. Three groups
 * are deliberately left out, because each of them differs between two runs that
 * played *identically* - hashing them would report a divergence on every single
 * checkpoint while mobs, boss, projectiles, timers and RNG were byte-identical:
 *
 * - **`events`**: a mailbox of things that already happened - hits, kills,
 *   leaks, a boss appearing - drained by whoever renders. The browser drains it
 *   several times a second; a server re-simulating the window has nobody to
 *   hand them to. Nothing in it changes what happens next.
 * - **`pendingMs`**: wall-clock time not yet simulated, always under one 16 ms
 *   tick. A browser frame is 16.67 ms so the client carries a few milliseconds
 *   of residue; the server advances whole ticks and carries none. Both run the
 *   same tick sequence; only the moment the next tick lands moves.
 * - **The account economy** - `level`, `xpIntoLevel`, `lifetimeXp`, `gold` and
 *   `freePoints`. These are the server's, and they are reconciled
 *   *asynchronously*: the browser overwrites its copy when a checkpoint
 *   answers, which is a few frames after the tick that answer describes, and an
 *   offline-farm grant can move them while a session is merely resumed. They
 *   also change nothing inside the simulation - damage, health and attack speed
 *   come from the three allocated point balances, which ARE hashed, and
 *   spending them retires the session anyway.
 *
 * What remains is exactly what decides the future: tick, phase, entity ids,
 * facing, health, accumulators, the boss countdown, every RNG stream, mobs,
 * boss, projectiles, the allocated points, the session's cumulative earnings -
 * the very numbers a checkpoint diffs to decide a reward - and the metrics
 * windows. A real desync still fires; a reconciliation no longer does.
 */
export function canonicalHuntSnapshotJson(snapshot: HuntSnapshot): string {
  return stringifyCanonical(deterministicPart(snapshot));
}

function deterministicPart(snapshot: HuntSnapshot): Record<string, unknown> {
  const { events: _events, pendingMs: _pendingMs, progression, bossEncounter, ...rest } = snapshot;
  return {
    ...rest,
    /*
     * The encounter WITHOUT its tier.
     *
     * The server knows the tier of a scheduled boss and the browser does not,
     * on purpose - so hashing it would put the two sides permanently out of
     * agreement and produce a correction on every single checkpoint, which is
     * the opposite of what a divergence check is for.
     *
     * Nothing is lost. A tier only changes the run through things that ARE
     * hashed: the boss's `size` once it is on the field, and
     * `earned.bossGoldBase` once it dies. A tier that had been tampered with
     * would show up in both the moment it mattered.
     */
    bossEncounter:
      bossEncounter === null
        ? null
        : {
            encounterId: bossEncounter.encounterId,
            ordinal: bossEncounter.ordinal,
            activated: bossEncounter.activated,
            // Hashed, unlike the tier: whether a boss is on its way back
            // changes what the next three minutes look like on both sides.
            retrying: bossEncounter.retrying === true,
          },
    progression: {
      damagePoints: progression.damagePoints,
      healthPoints: progression.healthPoints,
      attackSpeedPoints: progression.attackSpeedPoints,
    },
  };
}

/**
 * Decimal places kept when a float is hashed.
 *
 * `Math.atan2`, `Math.sin`, `Math.cos` and friends are explicitly
 * implementation-defined: the same engine family, V8, legitimately returns a
 * different last bit in Node than it does in Chromium. A homing projectile's
 * direction therefore comes out as -0.41673215370605354 on the server and
 * -0.4167321537060531 in the browser for the very same tick - identical play,
 * different bytes, and a correction on every checkpoint if hashed raw.
 *
 * Four decimals is a ten-thousandth of an arena unit. It swallows that noise
 * and nothing else: a divergence that matters moves a projectile by pixels,
 * changes a target, or drops an entity - never by 1e-16.
 */
const DIGEST_DECIMALS = 4;

function quantise(value: number): number {
  if (!Number.isFinite(value) || Number.isInteger(value)) {
    return value;
  }
  // `+0` keeps `-0` from serialising differently from `0`.
  return Number(value.toFixed(DIGEST_DECIMALS)) + 0;
}

function stringifyCanonical(value: unknown): string {
  if (typeof value === 'number') {
    return JSON.stringify(quantise(value)) ?? 'null';
  }
  if (value === null || typeof value === 'boolean') {
    return JSON.stringify(value) ?? 'null';
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stringifyCanonical).join(',')}]`;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stringifyCanonical(entryValue)}`)
      .join(',')}}`;
  }
  // bigint, function, symbol: never present in a snapshot by construction.
  throw new TypeError('snapshot contains a non-serialisable value');
}

/**
 * The hashing itself lives in `@kenocyber/shared` (`sha256Hex`): this package
 * stays platform-neutral and must not reach for Web Crypto. Callers hash the
 * canonical form above.
 */
